import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { SearchIcon } from '@heroicons/react/solid'
import { MenuAlt1Icon } from '@heroicons/react/outline'
import classNames from 'classnames'
import { toUpper, some } from 'lodash'
import { isPartnerDashboardUser } from 'lib/auth/dashboardRole'
import { destroyCookie } from 'nookies'
import { setUser } from 'state/redux/actions/user.actions'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const MobileHeader = ({ setSidebarOpen, user, openChangePasswordModal }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const isBookingsPage = history.location.pathname === '/bookings'
  const isCompanyRole = some(user?.roles, role => role?.type === 'company')
  const isPartnerRole = isPartnerDashboardUser(user)
  const showCompanyEcommerceLink = isCompanyRole && (user?.canManageBranding || user?.canManageDns)
  const showPartnerSettingsLink = isPartnerRole

  const fallbackInitial = () => {
    if (user?.displayName) {
      return toUpper(user.displayName.charAt(0))
    }
    const roles = user?.roles || []
    const isCompany = some(roles, role => role?.type === 'company')
    const isAdmin = some(roles, role => role?.type === 'admin' || role?.type === 'platform_admin' || role?.name === 'admin')
    if (isCompany) return 'C'
    if (isAdmin) return 'A'
    return 'U'
  }

  const handleLogout = e => {
    dispatch(setUser({}))

    destroyCookie({}, 'impersonateToken', { path: '/' })
    destroyCookie({}, 'isImpersonated', { path: '/' })
    destroyCookie({}, 'impersonateRole', { path: '/' })

    destroyCookie({}, 'accessToken', { path: '/' })
    destroyCookie({}, 'role', { path: '/' })
    destroyCookie({}, 'dashboardRole', { path: '/' })
    history.push('/login')
  }

  return (
    <div
      className={classNames('sticky top-0 flex-shrink-0 flex h-16 bg-white border-b border-gray-200', isBookingsPage ? 'sidebar:hidden' : 'lg:hidden')}
      style={{ zIndex: 999999 }}
    >
      <button
        type="button"
        className={classNames(
          'px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500',
          isBookingsPage ? 'sidebar:hidden' : 'lg:hidden'
        )}
        onClick={() => setSidebarOpen(true)}
      >
        <span className="sr-only">Open sidebar</span>
        <MenuAlt1Icon className="h-6 w-6" aria-hidden="true" />
      </button>
      <div className="flex-1 flex justify-end items-center px-4 sm:px-6 lg:px-8" style={{ zIndex: 999999 }}>
        {/* <div className="flex-1 flex">
          <form className="w-full flex md:ml-0" action="#" method="GET">
            <label htmlFor="search-field" className="sr-only">
              Search
            </label>
            <div className="relative w-full text-gray-400 focus-within:text-gray-600">
              <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5" aria-hidden="true" />
              </div>
              <input
                id="search-field"
                name="search-field"
                className="block w-full h-full pl-8 pr-3 py-2 border-transparent text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-0 focus:border-transparent focus:placeholder-gray-400 sm:text-sm"
                placeholder="Search"
                type="search"
              />
            </div>
          </form>
        </div> */}
        <div className="flex items-center flex-shrink-0" style={{ zIndex: 999999, position: 'relative' }}>
          <Menu as="div" className="relative" style={{ zIndex: 999999 }}>
            <div>
              <Menu.Button
                className={classNames(
                  'bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 flex-shrink-0',
                  isRTL ? 'ml-0' : ''
                )}
              >
                <span className="sr-only">Open user menu</span>
                {user.userProfile?.profile ? (
                  <img
                    className="h-8 w-8 rounded-full object-cover border-2 border-gray-200 bg-gray-300 flex-shrink-0"
                    src={user.userProfile.profile}
                    alt=""
                    onError={e => {
                      e.target.style.display = 'none'
                      const fallback = e.target.nextElementSibling
                      if (fallback) {
                        fallback.style.display = 'flex'
                      }
                    }}
                  />
                ) : null}
                <div
                  className={`h-8 w-8 rounded-full bg-gray-300 border-2 border-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium flex-shrink-0 ${
                    user.userProfile?.profile ? 'hidden' : ''
                  }`}
                >
                  {fallbackInitial()}
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className={classNames(
                  'fixed mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none',
                  isRTL ? 'origin-top-left left-4' : 'origin-top-right right-4'
                )}
                style={{ zIndex: 999999, top: 64 }}
              >
                <div className="py-1">
                  <Menu.Item
                    onClick={() => {
                      history.push('/profile')
                    }}
                  >
                    {({ active }) => (
                      <span className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm cursor-pointer')}>
                        {t('sidebar.profileSetting')}
                      </span>
                    )}
                  </Menu.Item>
                </div>

                <div className="py-1">
                  <Menu.Item onClick={() => openChangePasswordModal()}>
                    {({ active }) => <a className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm')}>{t('sidebar.changePassword')}</a>}
                  </Menu.Item>
                  {showCompanyEcommerceLink ? (
                    <Menu.Item onClick={() => history.push('/company/ecommerce')}>
                      {({ active }) => (
                        <span className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm cursor-pointer')}>
                          {t('sidebar.companyEcommerce')}
                        </span>
                      )}
                    </Menu.Item>
                  ) : null}
                  {showPartnerSettingsLink ? (
                    <Menu.Item onClick={() => history.push('/partner/settings')}>
                      {({ active }) => (
                        <span className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm cursor-pointer')}>
                          {t('sidebar.partnerSettings')}
                        </span>
                      )}
                    </Menu.Item>
                  ) : null}
                </div>
                <div className="py-1">
                  <Menu.Item onClick={handleLogout}>
                    {({ active }) => <a className={classNames(active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm')}>{t('sidebar.logout')}</a>}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  )
}

export default MobileHeader
